import { useEffect, useRef } from 'react';

import { App as CapacitorApp } from '@capacitor/app';
import type { PluginListenerHandle } from '@capacitor/core';

import { isNativeAndroidApp } from './platform';

type TAndroidBackHandler = () => boolean | Promise<boolean>;
type THandlerEntry = {
  priority: number;
  handler: TAndroidBackHandler;
};

const androidBackHandlers = new Map<number, THandlerEntry>();
let androidBackHandlerId = 0;

const subscribeAndroidBackHandler = (priority: number, handler: TAndroidBackHandler) => {
  const id = androidBackHandlerId + 1;
  androidBackHandlerId = id;

  androidBackHandlers.set(id, { priority, handler });

  return () => {
    androidBackHandlers.delete(id);
  };
};

const getSortedAndroidBackHandlers = () => {
  return Array.from(androidBackHandlers.values()).sort((left, right) => right.priority - left.priority);
};

export const useAndroidBackHandler = (priority: number, handler: TAndroidBackHandler) => {
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    if (!isNativeAndroidApp()) {
      return;
    }

    return subscribeAndroidBackHandler(priority, () => handlerRef.current());
  }, [priority]);
};

export const AndroidBackController = () => {
  useEffect(() => {
    if (!isNativeAndroidApp()) {
      return;
    }

    let listenerHandle: PluginListenerHandle | null = null;
    let isDisposed = false;

    const handleBackButton = async () => {
      const backHandlers = getSortedAndroidBackHandlers();

      for (const backHandler of backHandlers) {
        const isHandled = await backHandler.handler();
        if (isHandled) {
          return;
        }
      }

      await CapacitorApp.exitApp();
    };

    const registerBackButtonListener = async () => {
      listenerHandle = await CapacitorApp.addListener('backButton', () => {
        void handleBackButton();
      });

      if (isDisposed && listenerHandle) {
        await listenerHandle.remove();
        listenerHandle = null;
      }
    };

    void registerBackButtonListener();

    return () => {
      isDisposed = true;
      if (listenerHandle) {
        void listenerHandle.remove();
        listenerHandle = null;
      }
    };
  }, []);

  return null;
};
